import React, { useState, useEffect, useRef } from 'react';

const slides = [
  {
    icon: (
      <svg viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
        <path d="M8 14C8 10.686 10.686 8 14 8h28c3.314 0 6 2.686 6 6v20c0 3.314-2.686 6-6 6H30l-8 8v-8h-8c-3.314 0-6-2.686-6-6V14Z" stroke="white" strokeWidth="2.5" strokeLinejoin="round"/>
        <circle cx="20" cy="24" r="2.5" fill="white"/>
        <circle cx="28" cy="24" r="2.5" fill="white"/>
        <circle cx="36" cy="24" r="2.5" fill="white"/>
      </svg>
    ),
    gradient: 'from-[#0c2e6e] to-[#1a56b8]',
    accentColor: '#60a5fa',
    title: 'Stay Connected with\nYour Team',
    description:
      'Chat directly with your project managers and team leads. Get real-time guidance whenever you need it — right from the field.',
  },
  {
    icon: (
      <svg viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
        <rect x="12" y="8" width="32" height="40" rx="4" stroke="white" strokeWidth="2.5"/>
        <path d="M20 20h16M20 28h16M20 36h10" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx="40" cy="40" r="8" fill="white" fillOpacity="0.15" stroke="white" strokeWidth="2.5"/>
        <path d="M40 37v3.5l2 2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    gradient: 'from-[#0f4c35] to-[#1a7a55]',
    accentColor: '#34d399',
    title: 'Share Field Updates\nInstantly',
    description:
      'Report progress, flag issues, and send quick updates without leaving the app. Your manager sees your messages the moment you send them.',
  },
  {
    icon: (
      <svg viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
        <rect x="10" y="26" width="36" height="24" rx="5" stroke="white" strokeWidth="2.5"/>
        <path d="M18 26v-8a10 10 0 0120 0v8" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx="28" cy="38" r="4" stroke="white" strokeWidth="2.5"/>
        <path d="M28 42v4" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    ),
    gradient: 'from-[#4a1d96] to-[#7c3aed]',
    accentColor: '#a78bfa',
    title: 'Your Chats Are\nAlways Saved',
    description:
      'Every conversation is securely stored and synced across devices. Pick up right where you left off — nothing is ever lost.',
  },
];

const ChatEmptyState = ({ onStartChat }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const autoRef = useRef(null);
  const startXRef = useRef(0);

  const goTo = (i) => setActiveIndex((i + slides.length) % slides.length);

  const resetAuto = () => {
    clearInterval(autoRef.current);
    autoRef.current = setInterval(() => setActiveIndex(i => (i + 1) % slides.length), 3800);
  };

  useEffect(() => {
    autoRef.current = setInterval(() => setActiveIndex(i => (i + 1) % slides.length), 3800);
    return () => clearInterval(autoRef.current);
  }, []);

  const handleTouchStart = (e) => { startXRef.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - startXRef.current;
    if (Math.abs(dx) > 40) { goTo(activeIndex + (dx < 0 ? 1 : -1)); resetAuto(); }
  };

  const slide = slides[activeIndex];

  return (
    <div
      className="flex flex-col items-center px-6 pt-10 pb-6"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Illustration card */}
      <div
        key={activeIndex}
        className={`relative w-32 h-32 rounded-[28px] bg-gradient-to-br ${slide.gradient} flex items-center justify-center shadow-2xl overflow-hidden mb-8`}
        style={{ animation: 'csIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both' }}
      >
        {/* Ring decorations */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-28 h-28 rounded-full border border-white/10" />
          <div className="absolute w-20 h-20 rounded-full border border-white/10" />
        </div>
        {/* Accent dots */}
        <span
          className="absolute top-5 right-6 w-3 h-3 rounded-full opacity-70"
          style={{ backgroundColor: slide.accentColor }}
        />
        <span
          className="absolute bottom-8 left-7 w-2 h-2 rounded-full opacity-50"
          style={{ backgroundColor: slide.accentColor }}
        />
        {slide.icon}
      </div>

      {/* Fixed-height text block — prevents layout shift between slides */}
      <div className="w-full max-w-[300px] text-center" style={{ height: '138px', overflow: 'hidden' }}>
        <h2
          className="text-[18px] font-bold text-gray-900 mb-2.5 leading-snug whitespace-pre-line"
          style={{ animation: 'csFadeUp 0.3s ease both' }}
        >
          {slide.title}
        </h2>
        <p
          className="text-[13px] text-gray-500 leading-relaxed"
          style={{ animation: 'csFadeUp 0.38s ease both' }}
        >
          {slide.description}
        </p>
      </div>

      {/* Dot indicators */}
      <div className="flex items-center gap-2 mt-8 mb-8">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => { goTo(i); resetAuto(); }}
            className="rounded-full transition-all duration-300"
            style={{
              width: i === activeIndex ? 22 : 8,
              height: 8,
              backgroundColor: i === activeIndex
                ? 'var(--btn-primary-bg)'
                : '#d1d5db',
            }}
          />
        ))}
      </div>

      {/* CTA */}
      <button
        onClick={onStartChat}
        className="w-full max-w-[280px] py-3.5 px-6 bg-[var(--btn-primary-bg)] text-white font-semibold text-sm rounded-2xl active:scale-95 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        Start a Conversation
      </button>

      <p className="text-[11px] text-gray-400 text-center mt-3">
        Tap above to message your project manager
      </p>

      <style>{`
        @keyframes csIn {
          from { opacity: 0; transform: scale(0.85) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes csFadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ChatEmptyState;
